import { z } from 'zod';
import { timestamp, uuid } from './common.js';
/**
 * Google Drive connect / browse / import (F6). `drive_connections` itself
 * (+ `driveProvider`) lives in `./file.ts` alongside `fileSchema`/`uploadSchema`
 * (the entities it FKs) — this file holds the WS op DTOs.
 *
 * OAuth consent is NOT a new REST route: §5.8's closed REST-escape-hatch list
 * has exactly one OAuth callback, `GET /auth/google/callback` (F12 login).
 * Drive-connect reuses it — `drive.connect` returns a server-built consent
 * `authUrl` whose `state` is a server-signed nonce carrying
 * `{ purpose: 'drive_connect', userId }` (vs. `{ purpose: 'login' }`); the
 * shared callback branches on `state.purpose` before upserting either a
 * `sessions` row or a `drive_connections` row. This is a cross-team wiring
 * dependency on F0h/auth (owns the callback handler) — flagged for
 * `apps/server`, not solved here.
 */
// --- connect / status / disconnect --------------------------------------------
export const driveConnectStartResult = z.object({ authUrl: z.string().url() });
export const driveStatusResult = z.object({
    connected: z.boolean(),
    /** The connected Google account's email (display only). */
    email: z.string().email().nullable(),
    scope: z.string().nullable(),
    /** Present + in the past → F6 "token expired" re-auth prompt state. */
    expiresAt: timestamp.nullable(),
});
export const driveDisconnectResult = z.object({ disconnected: z.boolean() });
// --- browse (the picker modal) -------------------------------------------------
//
// Deliberately NOT `listQueryInput`/`paginated` (§5.9's contract is for LOCAL
// DB-backed queryable entities with a `total` count): this is a live,
// cursor-only proxy over the Drive API, which has no local total and its own
// opaque `pageToken`.
export const driveRemoteItemSchema = z.object({
    /** Drive-native file/folder id (opaque string, NOT a local uuid). */
    id: z.string().min(1),
    name: z.string().min(1),
    mimeType: z.string().min(1),
    isFolder: z.boolean(),
    /** Null for a Google-native file (Docs/Sheets) with no fixed byte size until exported. */
    size: z.number().int().nonnegative().nullable(),
    modifiedAt: timestamp.nullable(),
    iconUrl: z.string().url().nullable(),
});
export const driveListQuery = z.object({
    /** Drive-native folder id being browsed; null = Drive root. */
    folderId: z.string().nullable().default(null),
    pageToken: z.string().nullable().optional(),
    q: z.string().trim().max(200).optional(),
});
export const driveListResult = z.object({
    items: z.array(driveRemoteItemSchema),
    nextPageToken: z.string().nullable(),
});
// --- import ---------------------------------------------------------------------
/** `drive.import` — "Import selected" → one `ingest` job per file (F6). */
export const driveImportInput = z.object({
    fileIds: z.array(z.string().min(1)).min(1, 'errors:drive.import.empty').max(50, 'errors:drive.import.tooMany'),
    /** Target local explorer folder; null = root. */
    folderId: uuid.nullable().default(null),
});
export const driveImportOutcome = z.enum(['queued', 'duplicate', 'unsupported_type']);
/** One outcome per requested Drive file — partial-batch-failure is explicit, not thrown away. */
export const driveImportItemResult = z.object({
    driveFileId: z.string(),
    outcome: driveImportOutcome,
    /** Set only when `outcome==='queued'` — the `ingest` job to track via `job:{jobId}`. */
    jobId: uuid.nullable(),
});
export const driveImportResult = z.object({ items: z.array(driveImportItemResult) });
